import { Grid, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    ACCOUNT_MANDATORY_KEYS,
    CLIENT_PROFILE_STAGE_KEYS,
    FEATURE_NAMES,
    INCOME_SOURCE_MANDATORY_KEYS,
    PROFILE_DETAILS_MANDATORY_KEYS
} from '../../common/constants';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import { BasicButton, Typography } from '../../components/atoms';
import { ConfirmationCentered, InfoTooltip, StepperPills } from '../../components/molecules';
import {
    saveClient,
    setEnableConfirmConsoleNavigation,
    updateCurrentStep,
    syncAdviceClient,
    saveProposal
} from '../../features/client';
import {
    AdviceGoalPayloadType,
    removeLoader,
    saveGoal,
    showLoader,
    syncAdviceGoals,
    updateBanner,
} from '../../features/client-goals';
import { updateFactsheetModalVisibility, updateGlobalLoaderState } from '../../features/global/globalSlice';
import { CreateGoalsStep } from './CreateGoalsStep';
import { ReviewPlanDetailsStep } from './ReviewPlanDetailsStep';
import { cloneDeep } from 'lodash';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Banner } from '../../components/molecules/Banner/Banner';
import PDFViewer from '../../components/organisms/PDF/PDFViewer';
import GeneratedPlanPDF from '../../components/organisms/PDF/GeneratedPDFs/GeneratedPlanPDF';
import { convertDateFormat, formatDate, shouldPromptForAccounts, shouldPromptForIncome } from '../../common/helper';
import dayjs from 'dayjs';
import { StickyRegion } from '../../containers/sticky-region';
import {
    applyAccountFieldStripping,
    callRunpipe,
    checkFFEnabled,
    downloadPdfThroughBackend,
    PdfDownloadToastState
} from '../../utils';
import { ADVISOR_ROUTES } from '../../routes/routeConstants';
import { AddInvestmentAccounts } from '../../containers/add-investment-accounts';
import {
    ClientRowData,
    GoalRecommendationType,
    GoalTypes,
    IncomeSourcesType,
    InvestmentSourcesType
} from '../../common/types';
import { StepperPillsProps } from '../../components/molecules/StepperPills/StepperPills';
import { ReactComponent as AccountIcon } from '../../assets/icons/clientProfileRevamped.svg';
import { ReactComponent as GoalsIcon } from '../../assets/icons/goalsRevamped.svg';
import { ReactComponent as PlanDetailsIcon } from '../../assets/icons/planDetailsRevamped.svg';
import { ReactComponent as RiskToleranceIcon } from '../../assets/icons/riskToleranceRevamped.svg';
import RiskToleranceStep from '../ClientJourneyCommon/RiskToleranceStep';
import { CreateClientStepFF } from './CreateClientStepFF';
import { ScrollerOption } from '../../components/molecules/VerticalScroller/VerticalScroller';
import { getRiskToleranceOptions } from '../../common/constants';
import { CreatePdfReportsModal } from '../../modals/CreatePdfReports';
import { determineLastName, isRecordDataValid, normalizeAge } from '../../features/advisorConsole/utils';
import { Toast } from '../../components/molecules/Toast/Toast';
import { PdfDownloadToast } from '../../components/molecules/Toast/PdfDownloadToast';
import { MarketPortfolios, Portfolio, TenantSupportedWeightType } from '../../common/types';
import { ReviewStatus } from '../../components/molecules/AdminTable/Status';

interface ClientProfileProps {
    // eslint-disable-next-line no-unused-vars
    updateStep?: (step: number | ((prev: number) => number)) => void;
    disabled?: boolean;
}

const StyledBanner = styled('div')(({ theme }) => ({
    paddingBlock: theme.spacing(1)
}));

const StyleDivWithStatus = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
}));

const ClientJourney = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const global = useAppSelector((state) => state.global);
    const [openPDFModal, setOpenPDFModal] = useState(false);
    const [openPDFViewer, setOpenPDFViewer] = useState(false);
    const [goalsWithNoFundingSource, setGoalsWithNoFundingSource] = useState<AdviceGoalPayloadType['formFields']>([]);
    const [openClientDrawer, setOpenClientDrawer] = useState(false);
    const [isRelationship, setIsRelationship] = useState(false);
    const [showIncomeConfirmModal, setShowIncomeConfirmModal] = useState(false);
    const [pendingUpdateStep, setPendingUpdateStep] = useState<any>(null);
    const [showAccountConfirmModal, setShowAccountConfirmModal] = useState(false);
    const [pendingAccountUpdateStep, setPendingAccountUpdateStep] = useState<any>(null);
    const [factSheetsMissingToast, setFactSheetsMissingToast] = useState({ open: false, generating: false });
    const [toShowFactSheetsMissingToast, setToShowFactSheetsMissingToast] = useState(false);
    const [pdfGenerateToast, setPdfGenerateToast] = useState<PdfDownloadToastState>({ open: false, generating: false });
    const [activeDrawer, setActiveDrawer] = useState(false);
    const marketPortfolios: MarketPortfolios = useAppSelector((state) => state.global.marketPortfolios) as any;
    const weightTypes: TenantSupportedWeightType =
        useAppSelector((state) => state?.global?.globalConfig?.tenant_supported_weight_type) || 'both';
    const tenant = useAppSelector((state) => state?.auth?.user?.tenant);
    const tactical: Portfolio[] = useMemo(() => marketPortfolios?.tactical || [], [marketPortfolios]);
    const strategic: Portfolio[] = useMemo(() => marketPortfolios?.strategic || [], [marketPortfolios]);
    let riskProfileFromVerticalScroller = adviceClient?.riskProfile || 'Moderate';
    const [adviceGoalsResponse, setAdviceGoalsResponse] = useState<AdviceGoalPayloadType['response']>(
        adviceGoals?.response
    );
    const [goalsWithMissingDates, setGoalsWithMissingDates] = useState<AdviceGoalPayloadType['formFields']>([]);
    const [incomeWithMissingDetails, setIncomeWithMissingDetails] = useState<any>([]);
    const [accountWithMissingDetails, setAccountWithMissingDetails] = useState<any>([]);
    const [profileWithMissingDetails, setProfileWithMissingDetails] = useState<any>([]);

    const isClientAvailable = !!adviceClient?.clientFirstName;
    const isCoClientAvailable = !!adviceClient?.relationship[0]?.firstName;
    const riskProfiles = global?.goeConfig?.riskProfiles || [];
    const minMaxEquity = global?.goeConfig?.minMaxEquity || {};
    const RISK_TOLERANCE_OPTIONS = getRiskToleranceOptions(t, riskProfiles, minMaxEquity);
    const setRiskProfileFromVerticalScroller = async (selectedRisk: string) => {
        riskProfileFromVerticalScroller = selectedRisk;
        const updatedAdviceClient = {
            ...adviceClient,
            riskProfile: riskProfileFromVerticalScroller
        };
        await dispatch(saveClient(updatedAdviceClient));
        // We empty the response, when risk value has changed. ref: AEX-2235
        if (
            adviceGoals?.global?.isPlanRun && // is subsequent journey
            adviceGoals?.request[0]?.riskProfile !== riskProfileFromVerticalScroller && // risk value has changed
            adviceGoals?.response?.length > 0 // has response based on old risk profile
        ) {
            setAdviceGoalsResponse(adviceGoals?.response); // caching old response
            await dispatch(syncAdviceGoals({ ...adviceGoals, response: [] }));
        } else if (
            adviceGoals?.global?.isPlanRun && // is subsequent journey
            adviceGoals?.request[0]?.riskProfile === riskProfileFromVerticalScroller && // risk value has reverted
            adviceGoalsResponse?.length > 0 // has cached response based on old risk profile
        ) {
            setAdviceGoalsResponse([]); // clearing cached response
            await dispatch(syncAdviceGoals({ ...adviceGoals, response: adviceGoalsResponse })); // restore cached response if risk profile is reverted back
        }
    };
    const lastStep = useRef<number>(0);
    const setLastStep = (num: any) => {
        lastStep.current = num;
    };
    const banner = adviceGoals?.global?.banner;
    const isPlanRun = adviceGoals?.global?.isPlanRun;
    const bannerChangesExist = banner?.visible && banner.type === 'attention';
    const allFormFieldGoalIds = adviceGoals?.formFields?.map((goal) => goal.goalId) || [];
    const allResponseGoalIds = adviceGoals?.response?.map((goal) => goal.goalId) || [];
    const someGoalsHaveNoResponse = allFormFieldGoalIds.some((goalId: string) => !allResponseGoalIds.includes(goalId));
    const shouldRunPipe =
        lastStep.current == 2
            ? adviceGoals?.global?.enablePlanDetails ||
              adviceGoals.response?.length === 0 ||
              adviceGoals.response?.length !== adviceGoals.formFields?.length ||
              someGoalsHaveNoResponse ||
              bannerChangesExist
            : true;
    const { clientFirstName, clientLastName } = adviceClient;
    const clientFullName = `${clientFirstName}_${clientLastName}`;
    const goalDataRecomendation = adviceGoals?.recommendation?.find((rec: GoalRecommendationType) => {
        if (checkFFEnabled(FEATURE_NAMES['RECOMMENDATION_RECONCILIATION_FE_AEX-1848']))
            return (rec?.oneTimeTopUp || rec?.recomendedTenure || rec?.topUpAccumulation) && rec?.originalData;
    });

    interface GoalPortfolioMapping {
        goalName: string;
        portfolioName: string;
    }

    const getGoalPortfolioMappings = (adviceGoals: any, global: any): GoalPortfolioMapping[] => {
        if (!adviceGoals?.formFields || !adviceGoals?.response || !global?.portfolio) {
            return [];
        }

        return adviceGoals.formFields.map((goal: any) => {
            const goalResponse = adviceGoals.response.find((response: any) => response.goalId === goal.goalId);

            const portfolioId = goalResponse?.response?.body?.analysisReport?.recommendedPortfolioId;
            const portfolio = global.portfolio.find((p: any) => p.portfolioId === portfolioId);

            return {
                goalName: goal.data.goalName,
                portfolioName: portfolio?.portfolioName || '',
                portfolioUrl: portfolio?.portfolioUrl || ''
            };
        });
    };

    const goalPortfolioMappings = getGoalPortfolioMappings(adviceGoals, global);

    const checkUrlsInSource = (source: any) => {
        if (!Array.isArray(source) || source.length === 0) {
            return { allValid: false, fewValid: false };
        }

        let allValid = true;
        let fewValid = false;

        for (const portfolio of source) {
            const url = portfolio.portfolioUrl;
            const isValidUrl = !!url && url !== 'N/A' && url.startsWith('http');
            if (isValidUrl) {
                fewValid = true;
            } else {
                allValid = false;
            }
        }
        return { allValid: allValid, fewValid: fewValid };
    };

    const handleInvalidUrlToaster = useCallback(() => {
        setFactSheetsMissingToast({ open: true, generating: false });
    }, []);

    useEffect(() => {
        setGoalsWithNoFundingSource(
            adviceGoals?.formFields?.filter((goal) => {
                const selectedIncomeSources =
                    goal.data.sources.incomeSource?.filter((s: { isSelected: any }) => s.isSelected) || [];
                const selectedInvestmentSources =
                    goal.data.sources.investmentSource?.filter((s: { isSelected: any }) => s.isSelected) || [];
                const allSelectedSources = [...selectedIncomeSources, ...selectedInvestmentSources];

                // Check if no sources selected at all
                if (allSelectedSources.length === 0) return true;

                // Check for deleted income sources
                const hasDeletedIncomeSource = selectedIncomeSources.some(
                    (source: { accountNumber: string }) =>
                        !adviceClient?.accounts?.incomeSources?.find((ac) => ac.accountId === source.accountNumber)
                );

                // Check for deleted investment sources
                const hasDeletedInvestmentSource = selectedInvestmentSources.some(
                    (source: { accountNumber: string }) =>
                        !adviceClient?.accounts?.investmentsSources?.find((ac) => ac.accountId === source.accountNumber)
                );

                // Retirement goals must have at least one investment source
                if (goal.data.goalType === 'retirement' && selectedInvestmentSources.length === 0) {
                    return true;
                }

                return hasDeletedIncomeSource || hasDeletedInvestmentSource;
            })
        );
        const goalsMissingDates = adviceGoals?.formFields?.filter((goal) => {
            if (goal.data.goalType === GoalTypes.Retirement) {
                const retirementGoalData: any = goal.data;

                const isRetirementDateMissing =
                    !retirementGoalData.planStartRetirement ||
                    retirementGoalData.planStartRetirement === 'Invalid Date';
                const isPlanningDateMissing =
                    !retirementGoalData.targetDate || retirementGoalData.targetDate === 'Invalid Date';

                return isRetirementDateMissing || isPlanningDateMissing;
            }
            return false;
        });

        setGoalsWithMissingDates(goalsMissingDates || []);
        const incomeWithMissingDetails = adviceClient?.accounts?.incomeSources?.filter(
            (incomeSource: IncomeSourcesType) => {
                return !isRecordDataValid(incomeSource, INCOME_SOURCE_MANDATORY_KEYS, true, false);
            }
        );

        const accountWithMissingDetails = adviceClient?.accounts?.investmentsSources?.filter(
            (source: InvestmentSourcesType) => {
                return !isRecordDataValid(source, ACCOUNT_MANDATORY_KEYS, false, true);
            }
        );

        const ProfileData = [...createData()];
        const profileWithMissingDetails = ProfileData?.filter((profile) => {
            return !isRecordDataValid(profile, PROFILE_DETAILS_MANDATORY_KEYS);
        });

        setIncomeWithMissingDetails(incomeWithMissingDetails || []);
        setAccountWithMissingDetails(accountWithMissingDetails || []);
        setProfileWithMissingDetails(profileWithMissingDetails || []);
        if (
            incomeWithMissingDetails?.length > 0 ||
            accountWithMissingDetails?.length > 0 ||
            profileWithMissingDetails?.length > 0
        ) {
            dispatch(updateCurrentStep(0));
        }
    }, [adviceClient, adviceGoals]);

    const isProfileNFinanceStepInErrorState = useMemo(() => {
        return (
            incomeWithMissingDetails?.length > 0 ||
            accountWithMissingDetails?.length > 0 ||
            profileWithMissingDetails?.length > 0
        );
    }, [incomeWithMissingDetails, accountWithMissingDetails, profileWithMissingDetails]);

    const getBannerForNoFS = (step: string, { updateStep }: ClientProfileProps) => {
        let message = t('FS_ERROR_BANNER_MSG');
        const allSelectedSources = [
            ...goalsWithNoFundingSource[0]?.data.sources.incomeSource,
            ...goalsWithNoFundingSource[0]?.data.sources.investmentSource
        ];
        // For multiple goals with this error we show above original msg
        if (goalsWithNoFundingSource?.length > 1) message = t('FS_ERROR_BANNER_MSG');
        // custom banner msg for Ret goals with 0 Acc ref: JIRA/AEX-1811?focusedCommentId=1771314
        else {
            if (
                step !== 'CLIENT_ONBOARDING_STEP1_FF' &&
                step !== 'CLIENT_ONBOARDING_STEP4' &&
                step !== 'CLIENT_PROFILE_RISK_TOLERANCE_HEADING'
            )
                message = t('FS_RET_ERROR_BANNER_MSG_IN_STEP2');
            if (
                goalsWithNoFundingSource[0].data.goalType === 'retirement' &&
                (goalsWithNoFundingSource[0].data.sources.investmentSource?.length === 0 ||
                    allSelectedSources.find(
                        (source) =>
                            source.isSelected &&
                            ![
                                ...adviceClient?.accounts?.incomeSources,
                                ...adviceClient?.accounts?.investmentsSources
                            ].find((ac) => ac.accountId === source.accountNumber)
                    ))
            )
                message = t('FS_RET_ERROR_BANNER_MSG_IN_STEP1AND3');
        }
        const showHandleTryAgain = step !== 'CLIENT_ONBOARDING_STEP2'; // don't show in step 2
        return (
            <StyledBanner>
                <Banner
                    type={'attention'}
                    {...(showHandleTryAgain && {
                        handleTryAgain: async () => {
                            await dispatch(syncAdviceGoals({ ...adviceGoals, response: [] }));
                            updateStep?.(2);
                            await dispatch(updateCurrentStep(2));
                        }
                    })}
                    message={message}
                />
            </StyledBanner>
        );
    };

    const getBannerForMissingDates = (step: string, { updateStep }: ClientProfileProps) => {
        const showHandleTryAgain = step !== 'CLIENT_ONBOARDING_STEP2';
        const message = t('GOAL_DATE_ERROR_BANNER_MSG');
        return (
            <StyledBanner>
                <Banner
                    {...(showHandleTryAgain && {
                        handleTryAgain: async () => {
                            updateStep?.(2);
                            await dispatch(updateCurrentStep(2));
                        }
                    })}
                    type={'attention'}
                    message={message}
                />
            </StyledBanner>
        );
    };

    const createData = (): ClientRowData[] => {
        const data: ClientRowData[] = [];

        if (isClientAvailable) {
            data.push({
                id: 'client',
                name: `${adviceClient.clientFirstName ?? ''} ${adviceClient.clientLastName ?? ''}`.trim(),
                lastName: determineLastName(adviceClient?.clientFirstName, adviceClient?.clientLastName),
                type: 'Client',
                dob: adviceClient.clientDob ? formatDate(convertDateFormat(adviceClient.clientDob)) : '-',
                retirementAge: normalizeAge(adviceClient.retirementAge, '65'),
                planningAge: normalizeAge(adviceClient.planningAge, '86')
            });
        }
        if (isCoClientAvailable) {
            const coClient = adviceClient.relationship[0];

            data.push({
                id: 'coClient',
                name: `${coClient?.firstName ?? ''} ${coClient?.lastName ?? ''}`.trim(),
                lastName: determineLastName(coClient?.firstName, coClient?.lastName),
                type: 'Co-Client',
                dob: coClient?.age ? formatDate(convertDateFormat(coClient?.age)) : '-',
                retirementAge: normalizeAge(coClient?.retirementAge, '65'),
                planningAge: normalizeAge(coClient?.planningAge, '86')
            });
        }
        return data;
    };

    const getBannerForProfileMissingDetails = (step: string, { updateStep }: ClientProfileProps) => {
        const showHandleTryAgain = step !== 'CLIENT_ONBOARDING_STEP1_FF';
        const message = t('DETAILS_MISSING_BANNER_MSG');
        const linkLabel = t('REVIEW_PROFILE_AND_FINANCES');
        return (
            <StyledBanner>
                <Banner
                    {...(showHandleTryAgain && {
                        handleTryAgain: async () => {
                            updateStep?.(0);
                            await dispatch(updateCurrentStep(0));
                        }
                    })}
                    type={'attention'}
                    message={message}
                    hyperlinkLabelText={linkLabel}
                />
            </StyledBanner>
        );
    };

    const getBannerForAddFunding = (goalDataRecomendation?: any) => {
        const message = `${t('FS_ALERT_BANNER_MSG', {
            goalName: goalDataRecomendation?.originalData?.formFields?.goalName
        })}`;
        return (
            <StyledBanner>
                <Banner
                    type={'alert'}
                    handleTryAgain={() => {
                        setActiveDrawer(true);
                    }}
                    message={message}
                />
            </StyledBanner>
        );
    };

    const calculateProbabilityOfSuccessForProposal = () => {
        const clonedAdviceGoals = cloneDeep(adviceGoals);
        let probabilityOfSuccessArray: number[] = [];
        const modifiedAdviceGoalsFormFields = clonedAdviceGoals.formFields.map((item: any) => {
            const goalJson = clonedAdviceGoals?.response?.find(
                (itemResponse: any) => item.goalId == itemResponse?.goalId
            );
            const analysisReport = goalJson?.['response']?.['body']?.['analysisReport'];

            const portfolio = global.portfolio?.filter(
                (_sPort) => _sPort.portfolioId === analysisReport?.['recommendedPortfolioId']
            );

            const groupedPortfolio = portfolio?.[0]?.assets?.map((_type: any) => ({
                type: _type.type,
                totalAllocatedvalue: `${_type.totalAllocatedvalue}%`
            }));
            // get equity fixed income
            const equityFixedIncomeCash = groupedPortfolio?.reduce(
                (
                    acc: { equity: number; fixedIncome: number; cash: number },
                    item: { type: string; totalAllocatedvalue: string }
                ) => {
                    if (item.type === 'Equity') {
                        acc.equity += parseFloat(item.totalAllocatedvalue);
                    } else if (item.type === 'Fixed Income') {
                        acc.fixedIncome += parseFloat(item.totalAllocatedvalue);
                    } else if (item.type === 'Cash') {
                        acc.cash += parseFloat(item.totalAllocatedvalue);
                    }
                    return acc;
                },
                { equity: 0, fixedIncome: 0, cash: 0 }
            );
            const equityFixedIncomeString = `${equityFixedIncomeCash?.equity} / ${equityFixedIncomeCash?.fixedIncome} / ${equityFixedIncomeCash?.cash}`;

            // get probability of success
            const probabilityOfSuccess = Math.round(Number(analysisReport?.['currentGoalProbability'] * 100));
            item.data.probabilityOfSuccess = probabilityOfSuccess;
            item.data.equityFixedIncome = analysisReport ? equityFixedIncomeString : '';
            // get updated recommendation values from request
            const recommendations = clonedAdviceGoals?.recommendation?.find((rec: any) => rec.goalId === item.goalId);
            const requestData = clonedAdviceGoals?.request?.find((req: any) => req.goalId === item.goalId) || {};
            if (recommendations?.topUpDecumulation) {
                item.data.targetedRetirementIncome = requestData.targetedRetirementIncome;
            }
            if (recommendations?.recomendedTenure) {
                if (item.data.goalType === 'retirement')
                    item.data.planStartRetirement = requestData?.planStartRetirement;
                else {
                    item.data.targetDate = requestData?.targetDate;
                    item.data.goalDate = requestData?.goalDate;
                }
            }
            probabilityOfSuccessArray.push(probabilityOfSuccess);
            return item;
        });
        return { modifiedAdviceGoalsFormFields, probabilityOfSuccessArray };
    };

    const handleSaveProposal = async () => {
        try {
            const { modifiedAdviceGoalsFormFields, probabilityOfSuccessArray } =
                calculateProbabilityOfSuccessForProposal();
            const timestamp = dayjs().format('YYYY-MM-DD HH:mm:ss.SSSSSS');
            const canEnablePDFGenerate = adviceGoals.global.banner?.type !== 'attention';
            const keysToRemove = ['hasUnsavedChanges', 'enableConfirmConsoleNavigation', 'error'];
            const profilePayload: any = { ...adviceClient };
            keysToRemove.forEach((key) => {
                if (key in profilePayload) delete profilePayload[key];
            });

            const proposalsPayload = {
                _id: adviceGoals._id,
                apiName: adviceGoals.apiName || '',
                apiVer: adviceGoals.apiVer || '',
                assignedClientId: adviceGoals.assignedClientId || adviceClient._id,
                tenant: adviceGoals.tenant || 'advice',
                formFields: checkFFEnabled(FEATURE_NAMES['BACKEND_MANAGED_ID_TIMESTAMPS_FE_AEX-3021'])
                    ? modifiedAdviceGoalsFormFields.map((field: any) => {
                          const reduxField = adviceGoals.formFields?.find((rf: any) => rf.goalId === field.goalId);
                          const updateFlag = reduxField?.data?.update;
                          return {
                              ...field,
                              data: {
                                  ...field.data,
                                  update:
                                      updateFlag !== undefined
                                          ? updateFlag
                                          : field.data?.update !== undefined
                                          ? field.data.update
                                          : false
                              }
                          };
                      })
                    : modifiedAdviceGoalsFormFields,
                isMultiGoal: adviceGoals.isMultiGoal || false,
                iwsApplied: adviceGoals.iwsApplied || false,
                goeConfigEmail: adviceGoals.goeConfigEmail || '',
                request: adviceGoals.request || [],
                response: adviceGoals.response || [],
                recommendation: adviceGoals.recommendation || [],
                global: {
                    ...adviceGoals?.global,
                    status: 'complete',
                    ...(!adviceGoals.global.planCreationDate && { planCreationDate: timestamp }),
                    lastUpdateDate: timestamp,
                    enablePDFGenerate: canEnablePDFGenerate,
                    probabilityOfSuccess: Math.min(...probabilityOfSuccessArray),
                    enablePlanDetails: true
                }
            };
            const combinedPayload = {
                profile: profilePayload,
                proposals: proposalsPayload,
                tenant: tenant
            };
            await dispatch(saveProposal(combinedPayload));
        } catch (error) {
            console.error('Error in handleSaveProposal:', error);
        }
    };

    const prepareForReviewPlanStep = async () => {
        // add other clean up activities before review plan step
        // const popupEnabled = adviceClient?.enableConfirmConsoleNavigation;
        if (goalsWithNoFundingSource && goalsWithNoFundingSource?.length > 0) return;
        await callRunpipe(
            global?.goeConfig?.reallocSchedules ?? [],
            global?.orgInfo?.orgEmail,
            adviceClient,
            adviceGoals,
            dispatch
        );
        dispatch(setEnableConfirmConsoleNavigation(true));
        // dispatch(setEnableConfirmConsoleNavigation(popupEnabled));
    };

    //styled elements
    const JourneyContainer = styled('main')(() => ({
        display: 'flex',
        flexDirection: 'row',
        padding: '10px 40px 10px 40px',
        flexGrow: 1
        // marginBottom: '80px'
        // maxWidth: theme.breakpoints.values.lg // let's not add a maxWidth until we have a solution for the LeftPanel position
    }));

    // Validation function to check if client can continue to next step
    const isClientValidForNextStep = (adviceClient: any) => {
        const hasFirstName = adviceClient?.clientFirstName && adviceClient.clientFirstName.trim() !== '';
        const hasLastName = adviceClient?.clientLastName && adviceClient.clientLastName.trim() !== '';

        // Check if either income sources or investment sources have items
        const hasIncomeSources = adviceClient?.accounts?.incomeSources?.length > 0;
        const hasInvestmentSources = adviceClient?.accounts?.investmentsSources?.length > 0;

        // Return true only if all conditions are met
        return (
            hasFirstName &&
            hasLastName &&
            (hasIncomeSources || hasInvestmentSources) &&
            !isProfileNFinanceStepInErrorState
        );
    };

    // Validation function for risk tolerance step
    const isRiskToleranceStepValid = (adviceClient: any) => {
        return !!(
            riskProfileFromVerticalScroller ||
            (adviceClient?.riskProfile && adviceClient.riskProfile.trim() !== '') ||
            adviceClient?.step > 1
        );
    };

    // handling continue to next step button click from risk tolerance step
    const handleSyncRiskTolerance = async () => {
        // Create unique stages array
        const existingStages = adviceClient?.stages || [];
        const newStages = [...existingStages, CLIENT_PROFILE_STAGE_KEYS[2], CLIENT_PROFILE_STAGE_KEYS[3]];
        const uniqueStages = Array.from(new Set(newStages));

        // API call to persist riskProfile and stages
        const updatedAdviceClient = {
            ...adviceClient,
            riskProfile: riskProfileFromVerticalScroller,
            stages: uniqueStages,
            step: adviceClient.step + 1
        };
        await dispatch(saveClient(applyAccountFieldStripping(updatedAdviceClient, isPlanRun)));

        // Reset goals response if it exists
        if (adviceGoals?.response?.length > 0) {
            let goalsData: typeof adviceGoals = JSON.parse(JSON.stringify(adviceGoals));
            goalsData.response = [];
            goalsData.global = {
                ...goalsData.global,
                status: 'incomplete',
                enablePDFGenerate: false,
                enablePlanDetails: false
            };
            await dispatch(saveGoal(goalsData));
        }
    };

    // Usage example with your existing ContinueToNextStep component:
    const ContinueToNextStep = ({ updateStep, disabled }: ClientProfileProps) => {
        return (
            <BasicButton
                variant="contained"
                onClick={async () => {
                    if (adviceClient.step === 0 && shouldPromptForIncome(adviceClient)) {
                        setPendingUpdateStep(() => updateStep);
                        setShowIncomeConfirmModal(true);
                        return;
                    }
                    if (adviceClient.step === 0 && shouldPromptForAccounts(adviceClient)) {
                        setPendingAccountUpdateStep(() => updateStep);
                        setShowAccountConfirmModal(true);
                        return;
                    }
                    updateStep?.((prev) => prev + 1);
                    // update the step in redux
                    await dispatch(updateCurrentStep(adviceClient.step + 1));
                    await dispatch(
                        saveClient(
                            applyAccountFieldStripping({ ...adviceClient, step: adviceClient.step + 1 }, isPlanRun)
                        )
                    );
                }}
                disabled={disabled}
            >
                {t('CLIENT_ONBOARDING_CONTD_BTN')}
            </BasicButton>
        );
    };

    // Continue button for risk tolerance step
    const ContinueFromRiskToleranceStep = ({ updateStep, disabled }: ClientProfileProps) => {
        const isValidForNextStep = isRiskToleranceStepValid(adviceClient) && isClientValidForNextStep(adviceClient);

        return (
            <BasicButton
                variant="contained"
                onClick={async () => {
                    updateStep?.((prev) => prev + 1);
                    if (adviceClient?.riskProfile !== riskProfileFromVerticalScroller) handleSyncRiskTolerance();
                    else
                        await dispatch(
                            saveClient(
                                applyAccountFieldStripping({ ...adviceClient, step: adviceClient.step + 1 }, isPlanRun)
                            )
                        );
                }}
                disabled={disabled || !isValidForNextStep}
            >
                {t('CLIENT_ONBOARDING_CONTD_BTN')}
            </BasicButton>
        );
    };

    const handleIncomeConfirm = async () => {
        setShowIncomeConfirmModal(false);
        if (pendingUpdateStep) {
            // Execute the original continue logic correctly
            pendingUpdateStep?.((prev: number) => prev + 1);
            await dispatch(updateCurrentStep(adviceClient.step + 1));
            await dispatch(
                saveClient(applyAccountFieldStripping({ ...adviceClient, step: adviceClient.step + 1 }, isPlanRun))
            );
        }
        setPendingUpdateStep(null);
    };

    const handleIncomeCancel = () => {
        setShowIncomeConfirmModal(false);
        setPendingUpdateStep(null);
    };

    const handleAccountConfirm = async () => {
        setShowAccountConfirmModal(false);
        if (pendingAccountUpdateStep) {
            // Execute the original continue logic correctly
            pendingAccountUpdateStep?.((prev: number) => prev + 1);
            await dispatch(updateCurrentStep(adviceClient.step + 1));
            await dispatch(
                saveClient(applyAccountFieldStripping({ ...adviceClient, step: adviceClient.step + 1 }, isPlanRun))
            );
        }
        setPendingAccountUpdateStep(null);
    };

    const handleAccountCancel = () => {
        setShowAccountConfirmModal(false);
        setPendingAccountUpdateStep(null);
    };

    const ContinueToReviewStep = ({ updateStep, disabled }: ClientProfileProps) => (
        <BasicButton
            variant="contained"
            onClick={async () => {
                if (shouldRunPipe) {
                    dispatch(updateGlobalLoaderState(true));
                    await prepareForReviewPlanStep();
                    dispatch(updateGlobalLoaderState(false));
                }
                updateStep?.((prev) => prev + 1);
                // update the step in redux
                await dispatch(updateCurrentStep(adviceClient.step + 1));
                setLastStep(0);
            }}
            disabled={disabled}
        >
            {t('CLIENT_ONBOARDING_CONTD_BTN')}
        </BasicButton>
    );

    // Initializing Stepper steps
    const CreateProfile = ({ updateStep }: ClientProfileProps) => (
        <div style={{ maxWidth: '100%' }}>
            <StickyRegion>
                <StyledBanner>
                    {(!isProfileNFinanceStepInErrorState &&
                        goalsWithNoFundingSource?.length > 0 &&
                        getBannerForNoFS('CLIENT_ONBOARDING_STEP1_FF', { updateStep })) ||
                        (goalDataRecomendation && getBannerForAddFunding(goalDataRecomendation))}
                    {!isProfileNFinanceStepInErrorState &&
                        goalsWithMissingDates?.length > 0 &&
                        getBannerForMissingDates('CLIENT_ONBOARDING_STEP1_FF', { updateStep })}
                    {isProfileNFinanceStepInErrorState &&
                        getBannerForProfileMissingDetails('CLIENT_ONBOARDING_STEP1_FF', { updateStep })}
                </StyledBanner>
                <Grid container justifyContent="space-between" alignItems="center" padding="16px 0">
                    <Typography
                        variant="headers.h2"
                        color="neutral.grey.500"
                        t={'CLIENT_ONBOARDING_STEP1_FF'}
                    ></Typography>
                    <ContinueToNextStep
                        updateStep={updateStep}
                        disabled={!isClientValidForNextStep(adviceClient)} // Use the new validation function
                    />
                </Grid>
            </StickyRegion>
            <CreateClientStepFF
                openClientDrawer={openClientDrawer}
                setOpenClientDrawer={setOpenClientDrawer}
                isRelationship={isRelationship}
                setIsRelationship={setIsRelationship}
            />
        </div>
    );

    const RiskTolerance = ({ updateStep }: ClientProfileProps) => (
        <div style={{ maxWidth: '100%' }}>
            <StickyRegion>
                <StyledBanner>
                    {(goalsWithNoFundingSource?.length > 0 &&
                        getBannerForNoFS('CLIENT_PROFILE_RISK_TOLERANCE_HEADING', {
                            updateStep
                        })) ||
                        (goalDataRecomendation && getBannerForAddFunding(goalDataRecomendation))}
                    {goalsWithMissingDates?.length > 0 &&
                        getBannerForMissingDates('CLIENT_PROFILE_RISK_TOLERANCE_HEADING', { updateStep })}
                </StyledBanner>
                <Grid container justifyContent="space-between" alignItems="center" padding="16px 0">
                    <Typography
                        variant="headers.h2"
                        color="neutral.grey.500"
                        t="CLIENT_PROFILE_RISK_TOLERANCE_HEADING"
                    ></Typography>
                    <ContinueFromRiskToleranceStep updateStep={updateStep} />
                </Grid>
            </StickyRegion>
            <RiskToleranceStep
                clientName={clientName}
                riskOptions={riskOptions}
                selectedRiskProfile={riskProfileFromVerticalScroller}
                updateRisk={setRiskProfileFromVerticalScroller}
                adviceClientRiskProfile={adviceClient?.riskProfile}
                updateAdviceClientRiskProfile={async () => {
                    const updatedAdviceClient = {
                        ...adviceClient,
                        riskProfile: riskProfileFromVerticalScroller
                    };
                    await dispatch(syncAdviceClient(updatedAdviceClient));
                }}
                minMaxEquity={minMaxEquity}
            />
        </div>
    );

    const CreateGoals = ({ updateStep }: ClientProfileProps) => (
        <div style={{ maxWidth: '100%' }}>
            <StickyRegion>
                <StyledBanner>
                    {(goalsWithNoFundingSource?.length > 0 &&
                        getBannerForNoFS('CLIENT_ONBOARDING_STEP2', { updateStep })) ||
                        (goalDataRecomendation && getBannerForAddFunding(goalDataRecomendation))}
                    {goalsWithMissingDates?.length > 0 &&
                        getBannerForMissingDates('CLIENT_ONBOARDING_STEP2', { updateStep })}
                </StyledBanner>
                <Grid container justifyContent="space-between" padding="16px 0" alignItems="center">
                    <Typography variant="headers.h2" color="neutral.grey.500" t="CLIENT_ONBOARDING_STEP2"></Typography>
                    <ContinueToReviewStep
                        updateStep={updateStep}
                        disabled={
                            goalsWithNoFundingSource?.length > 0 ||
                            !adviceGoals?.formFields ||
                            adviceGoals?.formFields?.length < 1 ||
                            goalsWithMissingDates?.length > 0 ||
                            isProfileNFinanceStepInErrorState
                        }
                    />
                </Grid>
            </StickyRegion>
            <CreateGoalsStep
                goalsWithNoFundingSource={goalsWithNoFundingSource}
                goalsWithMissingDates={goalsWithMissingDates}
            />
        </div>
    );

    const ReviewPlanDetails = ({ updateStep }: ClientProfileProps) => {
        const marketPortfolios: MarketPortfolios = useAppSelector((state) => state.global.marketPortfolios) as any;
        const modalVisibilityFlags = useMemo(() => {
            if (marketPortfolios?.showModal || marketPortfolios?.showBanner || marketPortfolios?.showPdfViewer) {
                return {
                    showModal: marketPortfolios.showModal,
                    showBanner: marketPortfolios.showBanner,
                    showPdfViewer: marketPortfolios.showPdfViewer
                };
            }
            const tacticalUrlsStatus = checkUrlsInSource(tactical);
            const strategicUrlsStatus = checkUrlsInSource(strategic);

            let modal = false;
            let banner = false;
            let pdfViewer = false;

            if (weightTypes === 'both') {
                const allUrlsPresent = tacticalUrlsStatus.allValid && strategicUrlsStatus.allValid;
                const fewUrlsPresent = tacticalUrlsStatus.fewValid || strategicUrlsStatus.fewValid;

                if (allUrlsPresent) {
                    modal = true;
                } else if (fewUrlsPresent) {
                    banner = true;
                } else {
                    pdfViewer = true;
                }
            } else if (weightTypes === 'strategic_only') {
                if (strategicUrlsStatus?.allValid) {
                    modal = true;
                } else if (strategicUrlsStatus?.fewValid) {
                    banner = true;
                } else {
                    pdfViewer = true;
                }
            } else {
                pdfViewer = true;
            }
            dispatch(
                updateFactsheetModalVisibility({ showModal: modal, showBanner: banner, showPdfViewer: pdfViewer })
            );
            return { showModal: modal, showBanner: banner, showPdfViewer: pdfViewer };
        }, [tactical, strategic, weightTypes]);

        const handlePdfGenerationClick = async () => {
            if (modalVisibilityFlags?.showModal) {
                setOpenPDFModal(true);
            } else if (modalVisibilityFlags?.showBanner) {
                setOpenPDFModal(true);
                setToShowFactSheetsMissingToast(true);
            } else if (modalVisibilityFlags?.showPdfViewer) {
                setOpenPDFModal(false);
                setPdfGenerateToast({ open: true, generating: true });
                setFactSheetsMissingToast({ open: false, generating: false });
                dispatch(updateGlobalLoaderState(true));
                try {
                    await triggerBackendDownload();
                } finally {
                    dispatch(updateGlobalLoaderState(false));
                    // Safety net: if the download path never resolved the toast,
                    // dismiss the in-progress one so it doesn't stay stuck.
                    setPdfGenerateToast((prev) => (prev.generating ? { open: false, generating: false } : prev));
                }
            }
        };
        const disablePDFGenerate = !adviceGoals.global?.enablePDFGenerate;
        return (
            <div style={{ maxWidth: '100%' }}>
                <StickyRegion>
                    <StyledBanner>
                        {goalsWithMissingDates?.length > 0 && goalsWithNoFundingSource?.length > 0 ? (
                            <>
                                {getBannerForMissingDates('CLIENT_ONBOARDING_STEP4', { updateStep })}
                                {getBannerForNoFS('CLIENT_ONBOARDING_STEP4', { updateStep })}
                            </>
                        ) : goalsWithMissingDates?.length > 0 ? (
                            getBannerForMissingDates('CLIENT_ONBOARDING_STEP4', { updateStep })
                        ) : goalsWithNoFundingSource?.length > 0 ? (
                            getBannerForNoFS('CLIENT_ONBOARDING_STEP4', { updateStep })
                        ) : goalDataRecomendation ? (
                            getBannerForAddFunding(goalDataRecomendation)
                        ) : (
                            isPlanRun &&
                            banner &&
                            banner.visible && (
                                <Banner
                                    type={banner.type || 'attention'}
                                    handleClose={() => {
                                        dispatch(updateBanner({ visible: false }));
                                    }}
                                    message={banner.message}
                                />
                            )
                        )}
                    </StyledBanner>
                    <Grid container justifyContent="space-between" padding="16px 0" alignItems="center">
                        <StyleDivWithStatus>
                            <Typography
                                variant="headers.h2"
                                color="neutral.grey.500"
                                t="CLIENT_ONBOARDING_STEP4"
                            ></Typography>
                            <div>
                                <InfoTooltip
                                    message={
                                        adviceGoals?.global?.status === 'complete'
                                            ? t('STATUS_PILL_COMPLETE_TEXT')
                                            : t('STATUS_PILL_INCOMPLETE_TEXT')
                                    }
                                    placement="bottom-start"
                                    style={{ paddingBlock: '1.5em' }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <ReviewStatus variant={adviceGoals?.global?.status || 'incomplete'} />
                                    </div>
                                </InfoTooltip>
                            </div>
                        </StyleDivWithStatus>
                        <Grid
                            sx={{
                                display: 'flex',
                                flexDirection: 'row'
                            }}
                        >
                            <BasicButton
                                variant="outlined"
                                disabled={disablePDFGenerate}
                                onClick={() => {
                                    handlePdfGenerationClick();
                                }}
                                loading={openPDFModal}
                            >
                                {t(
                                    modalVisibilityFlags?.showPdfViewer
                                        ? 'CREATE_PDF_REPORT'
                                        : 'CLIENT_ONBOARDING_Share_PDF_V2'
                                )}
                            </BasicButton>
                            <BasicButton
                                variant={
                                    (goalsWithNoFundingSource && goalsWithNoFundingSource?.length > 0) ||
                                    (goalsWithMissingDates && goalsWithMissingDates?.length > 0) ||
                                    isProfileNFinanceStepInErrorState
                                        ? 'outlined'
                                        : 'contained'
                                }
                                sx={{ marginLeft: '10px' }}
                                onClick={handleSaveProposal}
                                disabled={
                                    (goalsWithNoFundingSource && goalsWithNoFundingSource?.length > 0) ||
                                    (goalsWithMissingDates && goalsWithMissingDates?.length > 0) ||
                                    Boolean(goalDataRecomendation) ||
                                    !adviceClient?.enableConfirmConsoleNavigation
                                }
                            >
                                {t('CLIENT_ONBOARDING_STEP5_V2')}
                            </BasicButton>
                        </Grid>
                    </Grid>
                </StickyRegion>

                <ReviewPlanDetailsStep
                    updateStep={updateStep}
                    callRunpipe={callRunpipe}
                    goalsWithNoFundingSource={goalsWithNoFundingSource}
                />
            </div>
        );
    };
    // Revisit to optimize when Risk Tolerance step is included
    const checkStepDisabled = (stepNumber: number) => {
        if (stepNumber > 0 && isProfileNFinanceStepInErrorState) {
            return true;
        }
        if (stepNumber === adviceClient.step) return true;
        if (!adviceGoals?.global?.isPlanRun && stepNumber === 0 && !isClientValidForNextStep(adviceClient)) {
            return true;
        }
        if (
            !adviceGoals?.global?.isPlanRun &&
            stepNumber === 1 &&
            (!isRiskToleranceStepValid(adviceClient) || !isClientValidForNextStep(adviceClient))
        ) {
            return true;
        }
        if (
            !adviceGoals?.global?.isPlanRun &&
            stepNumber === 2 &&
            (!adviceClient?.riskProfile || !isClientValidForNextStep(adviceClient))
        )
            return true;
        if (
            stepNumber === 3 &&
            (adviceGoals?.formFields?.length === 0 ||
                (goalsWithNoFundingSource && goalsWithNoFundingSource?.length > 0) ||
                (goalsWithMissingDates && goalsWithMissingDates?.length > 0))
        )
            return true;
        if (stepNumber === 3 && (shouldRunPipe || someGoalsHaveNoResponse)) {
            return false;
        }
    };

    const journeyStepsFF: StepperPillsProps = {
        steps: [
            {
                id: ADVISOR_ROUTES.CLIENT_PROFILE,
                label: t('CLIENT_ONBOARDING_STEP1_FF'),
                icon: <AccountIcon />,
                status: isProfileNFinanceStepInErrorState
                    ? 'error'
                    : isClientValidForNextStep(adviceClient)
                    ? 'completed'
                    : ('pending' as const),
                content: <CreateProfile />,
                disabled: checkStepDisabled(0)
            },
            {
                id: ADVISOR_ROUTES.RISK_TOLERANCE,
                label: t('CLIENT_PROFILE_RISK_TOLERANCE_HEADING'),
                icon: <RiskToleranceIcon />,
                status: isProfileNFinanceStepInErrorState
                    ? ('pending' as const)
                    : adviceClient.step > 0 || adviceClient?.riskProfile
                    ? 'completed'
                    : ('pending' as const),
                content: <RiskTolerance />,
                disabled: checkStepDisabled(1)
            },
            {
                id: ADVISOR_ROUTES.GOALS,
                label: t('CLIENT_ONBOARDING_STEP2'),
                icon: <GoalsIcon />,
                status: isProfileNFinanceStepInErrorState
                    ? ('pending' as const)
                    : goalsWithNoFundingSource?.length > 0 || goalsWithMissingDates?.length > 0
                    ? 'error'
                    : adviceGoals?.formFields?.length !== 0
                    ? 'completed'
                    : ('pending' as const),
                content: <CreateGoals />,
                disabled: adviceClient.step === 1 ? false : checkStepDisabled(2)
            },
            {
                id: ADVISOR_ROUTES.PLAN_DETAILS,
                label: t('CLIENT_ONBOARDING_STEP4'),
                icon: <PlanDetailsIcon />,
                status: isProfileNFinanceStepInErrorState
                    ? ('pending' as const)
                    : adviceGoals.response?.length > 0 &&
                      goalsWithNoFundingSource?.length === 0 &&
                      goalsWithMissingDates?.length === 0
                    ? 'completed'
                    : adviceClient.step === 3 &&
                      adviceGoals?.formFields?.length > 0 &&
                      goalsWithNoFundingSource?.length === 0 &&
                      goalsWithMissingDates?.length === 0
                    ? 'pending'
                    : ('pending' as const),
                content: <ReviewPlanDetails />,
                disabled: checkStepDisabled(3)
            }
        ],
        currentStep: adviceClient.step,
        runPlan: prepareForReviewPlanStep,
        setLastStep: setLastStep,
        onStepClick: async (stepNumber) => {
            setLastStep(adviceClient.step);
            if (checkStepDisabled(stepNumber) === true) return;
            else if (checkStepDisabled(stepNumber) === false) {
                dispatch(showLoader());
                await prepareForReviewPlanStep();
                dispatch(removeLoader());
            }
            await dispatch(updateCurrentStep(stepNumber));
            if (stepNumber === 2 && adviceClient?.step === 1) {
                // Handling risk tolerance step separately
                if (adviceClient?.riskProfile !== riskProfileFromVerticalScroller) handleSyncRiskTolerance();
                else
                    await dispatch(
                        saveClient(
                            applyAccountFieldStripping({ ...adviceClient, step: adviceClient.step + 1 }, isPlanRun)
                        )
                    );
            }
        }
    };

    const generatePDFfilename = () =>
        `${clientFullName?.trim()}_${dayjs(new Date()).format('DD/MM/YY').replaceAll('/', '')}`;

    // Client name and risk options for RiskTolerance component
    const clientName = `${adviceClient?.clientFirstName || ''} ${adviceClient?.clientLastName || ''}`.trim();

    // Create risk options using organization data if available, otherwise use constants
    const riskOptions: ScrollerOption[] = useMemo(() => {
        return RISK_TOLERANCE_OPTIONS.map((option) => ({
            id: option.id,
            title: option.title,
            description: option.description
        }));
    }, [global?.orgInfo?.riskProfileAex?.[i18n.language] || global?.orgInfo?.riskProfileAex?.['en'], t]);

    const triggerBackendDownload = async () => {
        let timestamp = dayjs().format('MMDDYY');
        await downloadPdfThroughBackend(
            {
                tenant: tenant || '',
                proposalId: adviceGoals?._id || '',
                assignedClientId: adviceClient?._id || '',
                da_advice_id: adviceGoals?.da_advice_id || '',
                orgEmail: global?.orgInfo?.orgEmail || '',
                weightType: weightTypes || '',
                filename: `${adviceClient?.clientFirstName ?? ''}_${
                    adviceClient?.clientLastName ?? ''
                }_${timestamp}.pdf`,
                language: i18n.language
            },
            setPdfGenerateToast
        );
    };

    return (
        <>
            <CreatePdfReportsModal
                open={openPDFModal}
                handleClose={() => setOpenPDFModal(false)}
                handleSaveButtonAction={() => {
                    setOpenPDFModal(false);
                    setOpenPDFViewer(true);
                }}
                goalPortfolioMappings={goalPortfolioMappings}
                openPDFViewer={openPDFViewer}
                handleInvalidUrlToaster={handleInvalidUrlToaster}
                toShowFactSheetsMissingToast={toShowFactSheetsMissingToast}
                triggerBackendDownload={triggerBackendDownload}
            />
            <PDFViewer
                open={openPDFViewer}
                handleClose={() => setOpenPDFViewer(false)}
                filename={generatePDFfilename()}
                setPdfGenerateToast={setPdfGenerateToast}
            >
                <GeneratedPlanPDF />
            </PDFViewer>
            {factSheetsMissingToast.open && (
                <Toast
                    heading={t('FACTSHEETS_MISSING_TOAST_TEXT')}
                    type="alert"
                    messages={[]}
                    handleClose={() => setFactSheetsMissingToast({ open: false, generating: false })}
                    disabled={false}
                    isTimer={false}
                />
            )}
            <PdfDownloadToast
                state={pdfGenerateToast}
                onClose={() => setPdfGenerateToast({ open: false, generating: false })}
            />
            <JourneyContainer>
                <StepperPills {...journeyStepsFF} />
            </JourneyContainer>
            {activeDrawer && (
                <AddInvestmentAccounts
                    setOpenDrawer={(flag: boolean) => setActiveDrawer(flag)}
                    openDrawer={activeDrawer}
                    mode="edit"
                    callRunpipe={callRunpipe}
                    goalDataRecomendation={goalDataRecomendation}
                />
            )}
            {showIncomeConfirmModal && (
                <ConfirmationCentered
                    cancelButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT')}`}
                    confirmButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_DELETE_TEXT')}`}
                    title={`${t('PROCEED_WITHOUT_INCOME_TITLE')}`}
                    content={`${t('PROCEED_WITHOUT_INCOME_CONTENT')}`}
                    onCancellation={handleIncomeCancel}
                    onCloseIconClick={handleIncomeCancel}
                    onConfirmation={handleIncomeConfirm}
                    open
                />
            )}
            {showAccountConfirmModal && (
                <ConfirmationCentered
                    cancelButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT')}`}
                    confirmButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_DELETE_TEXT')}`}
                    title={`${t('PROCEED_WITHOUT_ACCOUNTS_TITLE')}`}
                    content={`${t('PROCEED_WITHOUT_ACCOUNTS_CONTENT')}`}
                    onCancellation={handleAccountCancel}
                    onCloseIconClick={handleAccountCancel}
                    onConfirmation={handleAccountConfirm}
                    open
                />
            )}
        </>
    );
};
export default ClientJourney;
